import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowUpRight, Check, ChevronLeft, Images, MapPin } from "lucide-react";
import { useApi, img, waLink, SITE_URL } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Reveal, ZoomWrap } from "@/components/site/Reveal";
import { RichText } from "@/components/site/RichText";
import { Lightbox } from "@/components/site/Lightbox";
import { InquiryForm } from "@/components/site/InquiryForm";
import { WhatsAppIcon } from "@/components/site/SocialIcons";
import NotFound from "@/pages/NotFound";
import { useLang } from "@/lib/i18n";
import { PopularBadge } from "@/components/site/PopularBadge";

const ytId = (url) => url?.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/)?.[1];

const PhotoGrid = ({ l, onOpen }) => {
  const { t, L } = useLang();
  const imgs = l.images.length ? l.images : [l.cover_image];
  return (
    <div className="relative grid h-[52vh] min-h-[360px] gap-3 md:grid-cols-4 md:grid-rows-2" data-testid="detail-photos">
      {imgs.slice(0, 5).map((src, i) => (
        <button key={src + i} onClick={() => onOpen(i)} className={`img-zoom overflow-hidden rounded-2xl ${i === 0 ? "md:col-span-2 md:row-span-2" : "hidden md:block"}`} data-testid={`detail-photo-${i}`}>
          <ZoomWrap className="h-full"><img src={img(src)} alt={`${L(l, "title")} ${i + 1}`} className="h-full w-full object-cover" /></ZoomWrap>
        </button>
      ))}
      {imgs.length > 1 && (
        <button onClick={() => onOpen(0)} className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-forest shadow-lg" data-testid="detail-view-all-photos">
          <Images className="h-4 w-4" /> {t("detail.photos", { n: imgs.length })}
        </button>
      )}
    </div>
  );
};

const Sidebar = ({ l, s }) => {
  const rent = l.kind === "rent";
  const { t, L, lang } = useLang();
  return (
    <aside className="space-y-6 lg:sticky lg:top-28" data-testid="detail-sidebar">
      <div className="rounded-2xl border border-clay bg-white p-7">
        <p className="font-display text-3xl font-black text-forest" data-testid="detail-price">{rent ? `${l.price_tier} · ${t("detail.perNight")}` : L(l, "price_label")}</p>
        {l.external_url && (
          <a href={l.external_url} target="_blank" rel="noreferrer" className="btn-primary mt-5 w-full" data-testid="detail-primary-cta">
            {rent ? <ArrowUpRight className="h-4 w-4" /> : <WhatsAppIcon className="h-4 w-4" />} {L(l, "cta_label") || (rent ? t("card.avail") : t("detail.chat"))}
          </a>
        )}
        {l.website_url && (
          <a href={l.website_url} target="_blank" rel="noreferrer" className="btn-outline mt-3 w-full" data-testid="detail-website-cta">{t("detail.website")}</a>
        )}
      </div>
      <div className="rounded-2xl border border-clay bg-linen p-7">
        <p className="eyebrow">{rent ? t("detail.qStay") : t("detail.reach")}</p>
        <h3 className="mt-2 font-display text-2xl font-black text-ink">{t("detail.contact", { name: s?.contact_name || "Blue" })}</h3>
        <div className="mt-3 space-y-1 text-sm font-semibold text-ink">
          <p>{s?.contact_email}</p>
          {s?.contact_whatsapp && <a href={waLink(s.contact_whatsapp)} target="_blank" rel="noreferrer" className="block text-forest hover:underline" data-testid="detail-contact-whatsapp">{t("common.telWa")}: {s.contact_phone_display}</a>}
          {!rent && <p className="pt-1 text-xs text-moss">{L(s, "broker_license")}</p>}
        </div>
        <div id="inquire" className="mt-6 scroll-mt-28"><InquiryForm key={lang} listing={l} successText={t("form.successListing", { title: L(l, "title") })} /></div>
      </div>
    </aside>
  );
};

const Body = ({ l }) => {
  const { t, L } = useLang();
  const vid = ytId(l.video_url);
  const tagline = L(l, "tagline");
  const desc = L(l, "description");
  return (
    <div className="lg:col-span-8">
      <h2 className="text-2xl font-black text-forest sm:text-3xl">{t("detail.glance")}</h2>
      <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="detail-specs">
        {L(l, "specs").map((s) => (
          <div key={s} className="flex items-center gap-3 rounded-xl border border-clay p-4 font-bold text-ink"><span className="h-2 w-2 rounded-full bg-sage" />{s}</div>
        ))}
      </div>
      <div className="mt-12"><p className="mb-6 text-lg font-semibold leading-relaxed text-ink">{tagline}</p>{desc !== tagline && <RichText text={desc} />}</div>
      {vid && (
        <div className="mt-12" data-testid="detail-video">
          <h2 className="mb-5 text-2xl font-black text-forest sm:text-3xl">{t("detail.video")}</h2>
          <div className="aspect-video overflow-hidden rounded-2xl"><iframe className="h-full w-full" src={`https://www.youtube.com/embed/${vid}`} title={`${L(l, "title")} video`} allowFullScreen loading="lazy" /></div>
        </div>
      )}
      {l.highlights.length > 0 && (
        <div className="mt-12" data-testid="detail-highlights">
          <h2 className="text-2xl font-black text-forest sm:text-3xl">{t("detail.highlights")}</h2>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {L(l, "highlights").map((h) => (
              <li key={h} className="flex gap-3 font-medium text-ink"><Check className="mt-0.5 h-5 w-5 shrink-0 text-forest" />{h}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function ListingDetail() {
  const { slug } = useParams();
  const { data: l, loading, error } = useApi(`/listings/${slug}`);
  const { data: s } = useApi("/settings");
  const [idx, setIdx] = useState(null);
  const { t, L, to, lang } = useLang();
  const { hash } = useLocation();
  useEffect(() => {
    if (l && hash === "#inquire") setTimeout(() => document.getElementById("inquire")?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
  }, [l, hash]);
  if (error) return <NotFound />;
  if (loading || !l) return <div className="container-x py-32 text-moss" data-testid="detail-loading">{t("common.loading")}</div>;
  const rent = l.kind === "rent";
  const title = L(l, "title");
  const photos = (l.images.length ? l.images : [l.cover_image]).map((src, i) => ({ src, alt: `${title} ${i + 1}` }));
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": rent ? "LodgingBusiness" : "RealEstateListing",
    name: title, description: L(l, "seo_description") || L(l, "tagline"), url: `${SITE_URL}${to(rent ? `/for-rent/${l.slug}` : `/${l.slug}`)}`, inLanguage: lang,
    image: l.images.map((i) => (i.startsWith("/media") ? SITE_URL + i : i)),
    address: { "@type": "PostalAddress", addressLocality: "Escamequita, San Juan del Sur", addressRegion: "Rivas", addressCountry: "NI" },
    ...(rent ? { priceRange: l.price_tier } : { offers: { "@type": "Offer", price: l.price_label.replace(/[^0-9]/g, ""), priceCurrency: "USD" } }),
  };
  return (
    <>
      <Seo title={L(l, "seo_title") || `${title} | Escamequita, Nicaragua`} description={L(l, "seo_description") || L(l, "tagline")} image={l.cover_image} jsonLd={jsonLd} />
      <section className="container-x pt-10" data-testid="listing-detail">
        <Link to={to(rent ? "/for-rent" : "/homes-and-land-for-sale")} className="inline-flex items-center gap-1 text-sm font-bold text-moss hover:text-forest" data-testid="detail-back-link">
          <ChevronLeft className="h-4 w-4" /> {rent ? t("detail.backRent") : t("detail.backSale")}
        </Link>
        <Reveal>
          <PopularBadge slug={l.slug} className="mt-5" />
          <h1 className="mt-5 text-4xl font-black leading-[1.05] tracking-tight text-forest sm:text-5xl lg:text-6xl" data-testid="detail-title">{title}</h1>
          {l.location && <p className="mt-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-sage"><MapPin className="h-4 w-4" />{L(l, "location")}</p>}
        </Reveal>
        <div className="mt-8"><PhotoGrid l={l} onOpen={setIdx} /></div>
        <div className="grid gap-12 py-16 lg:grid-cols-12">
          <Body l={l} />
          <div className="lg:col-span-4"><Sidebar l={l} s={s} /></div>
        </div>
      </section>
      {idx !== null && <Lightbox images={photos} index={idx} onChange={setIdx} onClose={() => setIdx(null)} />}
    </>
  );
}
